import React, { useMemo, useState } from 'react'

import { isObject, snakeCase, values } from 'lodash'
import { connect } from 'react-redux'
import { Button, Divider, InputNumber, message, notification } from 'antd'
import FormItem from 'antd/es/form/FormItem'
import { faEdit  } from '@fortawesome/pro-solid-svg-icons'
import { faSquarePlus } from '@fortawesome/pro-regular-svg-icons'

import InternshipForm from './InternshipForm'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { getSchoolName, getSchoolYearName, getSectionNameOrFull, getSectorName, Internship } from '../../../utils/entities/internship'
import { getIdOrNull, getRandomInt, isPositive } from '../../../utils'
import { isValid } from '../../../utils/validators'
import DataTable from '../../../Components/antd/Tables/DataTable/DataTable'
import { Action, DeleteAction } from '../../../Components/antd/Tables/DataTable/utils/actions'
import { dateGetters } from '../../../utils/entities/common'
import FormFooter from '../../../Components/antd/Forms/FormFooter'
import IconLabel, { FontSizes } from '../../../Components/icons/IconLabel'

import '../../../assets/quotas.scss'

export const BODY_STYLE = { display: 'flex', flexDirection: 'column', height: '93%', width: '100%' }
export const SELECT_STYLE = { width: '200px' }

message.config({
  top: 20,
  duration: 3,
  maxCount: 1,
  placement: 'topRight'
})

const QUANTITY = {
  MAX: 10,
  MIN: 1
}

const mapStateToProps = state => ({ t: selectTranslate(state) })

const BulkInternshipForm = ({ data, quantity: initQuantity = QUANTITY.MIN, disabled, sectors = [], validators, onDelete, onSubmit, onCancel, t }) => {
  const [formData, setFormData] = useState(isObject(data) ? new Internship(data) : data)
  const [quantity, setQuantity] = useState((initQuantity >= QUANTITY.MIN && initQuantity < QUANTITY.MAX) ? initQuantity : QUANTITY.MIN)

  const [internships, setInternships] = useState([])
  const [errorFields, setErrorFields] = useState([])
  const [replacedInternship, setReplacedInternship] = useState({ index: null, internship: null })

  const addToList = event => {
    const errors = isValid(formData, values(validators))
    setErrorFields(errors.keys)

    if (errors.keys.length > 0) {
      const errorMessages = t('common_form.error_fields', {
        fields: errors.keys.map(key => t('common.' + snakeCase(key))).join(', ')
      })

      notification.error({ message: errorMessages })
    } else if (formData.startDate > formData.endDate) {
      message.error(t('common_form.error_dates'))
    } else if (internships.length < QUANTITY.MAX) {
      const internshipsToAdd = []

      for(let iGenerate = 0; iGenerate < quantity && internships.length + internshipsToAdd.length < QUANTITY.MAX; iGenerate++) {
        let randomInt = getRandomInt()

        while (internships.some(i => i.id === randomInt) || internshipsToAdd.some(i => i.id === randomInt)) {
          randomInt = getRandomInt()
        }

        internshipsToAdd.push(new Internship({ ...formData, id: -randomInt }))
      }

      setReplacedInternship({ index: null, internship: null })
      setInternships(current => current.concat(internshipsToAdd))
      setQuantity(QUANTITY.MIN)
    }
  }

  const updateInternship = newData => setFormData(form => ({ ...form, ...newData }))

  const handleSubmit = () => {
    setInternships([])
    onSubmit(internships)
  }

  const removeInternship = internship => setInternships(internships => internships.filter(i => i.id !== internship.id))

  const replaceInternship = selected => {
    if (!getIdOrNull(replacedInternship.internship)) {
      const index = internships.findIndex(i => i.id === selected.id)

      removeInternship(selected)
      setReplacedInternship({ index, internship: selected })
    } else {
      const nextState = [...internships]
      nextState.splice(replacedInternship.index, 0, replacedInternship.internship)

      const index = nextState.findIndex(i => i.id === selected.id)
      nextState.splice(index, 1)

      setInternships(nextState)
      setReplacedInternship({ index, internship: selected })
    }
  }

  const handleCancel = () => {
    setInternships(current => {
      const nextState = [...current]
      nextState.splice(replacedInternship.index, 0, replacedInternship.internship)

      return nextState
    })

    setReplacedInternship({ index: null, internship: null })
  }

  const handleSelect = internship => {
    setQuantity(QUANTITY.MIN)
    setFormData(internship)
    replaceInternship(internship)
  }

  const actions = [
    new Action({ icon: faEdit, title: t('common.edit'), onClick: handleSelect}),
    new DeleteAction({ onDelete: removeInternship })
  ]

  const columns = useMemo(() => [
    { title: t('common.start_date'), key: dateGetters.startDate },
    { title: t('common.end_date'), key: dateGetters.endDate },
    { title: t('common.sector'), key: getSectorName },
    { title: t('common.section'), key: getSectionNameOrFull },
    { title: t('common.year'), key: getSchoolYearName },
    { title: t('common.school'), key: getSchoolName }
  ], [t])

  const isSubmitDisabled = useMemo(() => (
    !isPositive(quantity) || formData.startDate === null || formData.endDate === null || formData.sector === null || formData.section === null || disabled
  ), [quantity, formData.startDate, formData.endDate, formData.sector, formData.section, disabled])

  return (
    <div>
      <div className='w-100 pd-12'>
        <InternshipForm
          data={formData}
          disabled={disabled}
          sectors={sectors}
          errorFields={errorFields}
          onChange={updateInternship}
        />

        <div className='form-class' style={{ paddingLeft: '24px' }}>
          <FormItem label={<IconLabel icon={faSquarePlus} text={`${t('common.quantity')} ${t('common.maximum.parenthesis', { maximum: QUANTITY.MAX })}`} fontSize={FontSizes.SMALL} padding='0px' />}>
            <InputNumber min={QUANTITY.MIN} max={QUANTITY.MAX} value={quantity} disabled={disabled} onChange={setQuantity} />
          </FormItem>
        </div>

        <div className='flex-row w-100 jc-end'>
          {getIdOrNull(replacedInternship.internship) && (
            <div className='mr-4'>
              <Button onClick={handleCancel}>
                {t('modal.cancel_button')}
              </Button>
            </div>
          )}
          <div>
            <Button onClick={addToList} disabled={isSubmitDisabled}>
              {!getIdOrNull(replacedInternship.internship) ? t('bulk_form.buttons.add') : t('modal.ok_button.save')}
            </Button>
          </div>
        </div>
      </div>

      <Divider className='divider-4' />

      <div className='w-100 pd-12'>
        <DataTable
          actions={actions}
          columns={columns}
          data={internships}
        />
      </div>

      <FormFooter
        cancel={{ text: t('modal.cancel_button'), disabled }}
        submit={{ text: t('modal.ok_button.save'), disabled: isSubmitDisabled || internships.length === 0 }}
        remove={null}
        onSubmit={handleSubmit}
        onCancel={onCancel}
      />
    </div>
  )
}

export default connect(mapStateToProps)(BulkInternshipForm)